// The one place Discover decides how a row describes a place. The gems drawer,
// the coupon/raffle rows and the cluster modal all render what this returns —
// they previously each built the lines themselves, which is why they disagreed.
import { isPlaceholderValue, normalizeLabelText } from "./businessLocationLabel";
import { formatNearbyDistance } from "./nearbyDiscovery";

const SEPARATOR = " · ";
const DEFAULT_TITLE = "Time capsule";

export const MEMBER_EXCLUSIVE_TEASER_TITLE =
  "Only Insiders can open this one — joining is free";

// Discover's own bury wizard prefills the message body with this phrase followed
// by the place and/or address. It reaches the title slot via normalizeNearbyGem,
// which falls back to messageBody. The wizard imports this constant so the
// producer and the detector cannot drift apart.
export const FRIEND_CAPSULE_TITLE_PREFIX = "This place made me think of you:";

function isFriendBoilerplateTitle(value) {
  const candidate = normalizeLabelText(value);
  if (!candidate) return false;
  return candidate.startsWith(normalizeLabelText(FRIEND_CAPSULE_TITLE_PREFIX));
}

function str(value) {
  return String(value || "").trim();
}

function sameText(a, b) {
  return Boolean(a) && normalizeLabelText(a) === normalizeLabelText(b);
}

// Seeded titles are built as "Must Drink: <place>", so an exact match never fires
// and the row printed the place name twice — once inside the title, once beneath
// it. Containment catches that while leaving a business row's `company · location`
// line intact, since a company name is not a substring of its own message title.
function titleAlreadyNames(title, value) {
  const candidate = normalizeLabelText(value);
  if (!candidate) return false;
  return normalizeLabelText(title).includes(candidate);
}

// Keyed on capsuleType, which gems, offers and markers all carry — a single
// predicate for every surface. Gating this per-surface is what let the cluster
// modal ship without it.
export function isGatedMemberExclusive(source = {}, { isInsider = false } = {}) {
  const type = normalizeLabelText(source?.capsuleType).replace(/[-_\s]/g, "");
  return type === "memberexclusive" && !isInsider;
}

// A row that says only "name · address" gives no reason to pick it over the row
// beneath it. These are the four signals a person actually chooses on.
function buildSignalsLine(source = {}, locale = "") {
  const signals = source?.placeSignals && typeof source.placeSignals === "object"
    ? source.placeSignals
    : {};
  const rating = Number(signals.rating);
  const reviewCount = Number(signals.reviewCount);
  const priceLevel = Number(signals.priceLevel);
  // Not `Number(...)` — that coerces a missing/null distance to 0 and would wrongly
  // render "25 ft" for a row with no distance at all.
  const distance = source?.distanceMeters;

  const parts = [];
  if (Number.isFinite(rating) && rating > 0) {
    const reviews = Number.isFinite(reviewCount) && reviewCount > 0
      ? ` (${reviewCount.toLocaleString()})`
      : "";
    parts.push(`${rating.toFixed(1)} ★${reviews}`);
  }
  if (Number.isFinite(priceLevel) && priceLevel > 0) {
    parts.push("$".repeat(Math.min(priceLevel, 4)));
  }
  if (Number.isFinite(distance) && distance >= 0) {
    const formatted = formatNearbyDistance(distance, locale);
    if (formatted) parts.push(formatted);
  }
  return parts.join(SEPARATOR);
}

// Mirrors getMessageTitle in functions/discover.js. Duplicated deliberately: the
// server writes the title at seed time and the client repairs it at read time,
// and three strings are cheaper than plumbing the mapping across the boundary.
// If the server's wording changes, change it here too.
const QUICK_FIND_TITLE_PREFIX = {
  eat: "Must Eat",
  drink: "Must Drink",
  fun: "Must Do",
};

// Capsules seeded before the category-aware titles all read "Discover <place>",
// and no migration can repair the stored value: the category belongs to the
// search that surfaced the capsule, not to the place, so inferring it back from
// place types measured only 58% accurate. The row, though, knows exactly which
// button was tapped — so it can title correctly at read time without guessing
// and without writing anything.
//
// Deliberately narrow. It fires only for a public seeded capsule whose stored
// title is EXACTLY the legacy auto-generated form, so anything a human or Studio
// authored is invisible to it and correctly-titled capsules keep agreeing with
// their own detail page. Featured, sponsored, private and member-exclusive are
// excluded by allowlist rather than by enumerating them.
function resolveReadTimeQuickFindTitle(source = {}) {
  if (source?.seeded !== true) return "";
  if (normalizeLabelText(source?.capsuleType).replace(/[-_\s]/g, "") !== "public") return "";

  const prefix = QUICK_FIND_TITLE_PREFIX[str(source?.quickFindCategoryKey).toLowerCase()];
  if (!prefix) return "";

  const placeName = str(source?.locationName) || str(source?.placeName);
  if (!placeName) return "";

  const storedTitle = str(source?.messageTitle) || str(source?.title);
  if (storedTitle !== `Discover ${placeName}`) return "";

  return `${prefix}: ${placeName}`;
}

export function buildDiscoveryRowLabels(
  source = {},
  { isInsider = false, identityInHeader = false, locale = "" } = {}
) {
  const companyName = str(source?.companyName);
  const senderLabel = str(source?.senderLabel);
  const locationName = str(source?.locationName);
  const address = str(source?.address);

  // Studio auto-fills a blank subject from the place name, the business name or
  // the address, so a title matching any of those is not a real message title.
  const autoFilled = [address, locationName, companyName, str(source?.placeName)];

  const storedTitle = isGatedMemberExclusive(source, { isInsider })
    ? MEMBER_EXCLUSIVE_TEASER_TITLE
    : [source?.messageTitle, source?.title, source?.headline]
        .map(str)
        .find(
          (value) =>
            value && !isPlaceholderValue(value, autoFilled) && !isFriendBoilerplateTitle(value)
        ) ||
      str(source?.seedName) ||
      locationName ||
      address ||
      DEFAULT_TITLE;

  // Resolved before the identity and place de-duplication below, so those
  // comparisons run against the title the row will actually show.
  const title = resolveReadTimeQuickFindTitle(source) || storedTitle;

  // Compare the place against the identity we resolved, not the one we may be
  // about to hide — otherwise hiding it would let a duplicate place reappear.
  const identityRaw = companyName || senderLabel;
  // Containment, not equality, for the same reason as `place` below: a gem's
  // senderLabel falls back to the place name, so a seeded row resolved identity
  // to "Techno Viking" against the title "Must Do: Techno Viking" and printed it
  // twice. A business whose title happens to name it is redundant in the same way.
  const identity = identityInHeader || titleAlreadyNames(title, identityRaw) ? "" : identityRaw;

  const place =
    locationName && !sameText(locationName, identityRaw) && !titleAlreadyNames(title, locationName)
      ? locationName
      : "";

  const addressLine =
    address && !sameText(address, place) && !sameText(address, title) && !sameText(address, identityRaw)
      ? address
      : "";

  const secondary = [identity, place].filter(Boolean).join(SEPARATOR);
  const signals = buildSignalsLine(source, locale);

  // With no business and no named place, the address is the only thing left to
  // say — it moves up rather than leaving a gap.
  if (!secondary) return { title, secondary: addressLine, tertiary: "", signals };

  return { title, secondary, tertiary: addressLine, signals };
}
